import styled, { css } from "styled-components";
import { useEffect, useRef, useState } from "react";

/* ============================================================
   Shared primitives — Divine Futurism. Glass · gold · serif.
   Imported by every section. Do not duplicate these locally.
============================================================ */

export const Section = styled.section`
  position: relative;
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 110px 24px;
  z-index: 2;

  @media ${({ theme }) => theme.bp.md} {
    padding: 72px 20px;
  }
`;

/* uppercase gold eyebrow with flanking rule */
export const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.gold};
  margin-bottom: 16px;

  &::before {
    content: "";
    width: 28px;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => theme.gold}
    );
  }
`;

/* serif display heading with gold-leaf gradient */
export const SectionTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.serif};
  font-weight: 600;
  font-size: clamp(34px, 5.2vw, 58px);
  line-height: 1.05;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.white};

  em {
    font-style: italic;
    background: linear-gradient(
      100deg,
      ${({ theme }) => theme.goldSoft},
      ${({ theme }) => theme.gold} 45%,
      ${({ theme }) => theme.violet}
    );
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

export const SectionIntro = styled.p`
  font-size: 17px;
  line-height: 1.7;
  color: ${({ theme }) => theme.textMuted};
  max-width: 640px;
  margin-top: 16px;
`;

export const Head = styled.div`
  text-align: ${({ $center }) => ($center ? "center" : "left")};
  margin-bottom: 52px;
  ${({ $center }) =>
    $center &&
    css`
      display: flex;
      flex-direction: column;
      align-items: center;
    `}
  @media ${({ theme }) => theme.bp.md} {
    margin-bottom: 36px;
  }
`;

/* frosted stained-glass panel */
export const Glass = styled.div`
  position: relative;
  background: ${({ theme }) => theme.glass};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 16px;
  backdrop-filter: blur(14px);
  overflow: hidden;
  transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;

  /* illuminated top edge */
  &::before {
    content: "";
    position: absolute;
    inset: 0 0 auto 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => theme.borderHi},
      transparent
    );
  }
  &:hover {
    border-color: ${({ theme }) => theme.borderHi};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.border},
      0 18px 50px -18px ${({ theme }) => theme.violetGlow},
      0 0 40px -10px ${({ theme }) => theme.goldGlow};
    transform: translateY(-3px);
  }
`;

/* gold tag / relic chip */
export const Tag = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 11.5px;
  letter-spacing: 0.04em;
  padding: 5px 11px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.border};
  background: rgba(201, 168, 76, 0.06);
  color: ${({ theme }) => theme.goldSoft};
  white-space: nowrap;
  transition: all 0.25s ease;

  &:hover {
    border-color: ${({ theme }) => theme.borderHi};
    color: ${({ theme }) => theme.gold};
    box-shadow: 0 0 14px ${({ theme }) => theme.goldGlow};
  }
`;

export const TagGhost = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 11.5px;
  padding: 5px 11px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.borderViolet};
  color: ${({ theme }) => theme.textMuted};
  white-space: nowrap;
`;

/* buttons */
const btnBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 14.5px;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 13px 26px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.28s ease;
  border: 1px solid transparent;
`;

export const BtnPrimary = styled.a`
  ${btnBase};
  color: #0b0a14;
  background: linear-gradient(
    100deg,
    ${({ theme }) => theme.goldSoft},
    ${({ theme }) => theme.gold}
  );
  box-shadow: 0 0 24px -4px ${({ theme }) => theme.goldGlow};
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 38px -2px ${({ theme }) => theme.goldGlow};
  }
`;

export const BtnGhost = styled.a`
  ${btnBase};
  color: ${({ theme }) => theme.white};
  border-color: ${({ theme }) => theme.borderHi};
  background: ${({ theme }) => theme.glass};
  backdrop-filter: blur(8px);
  &:hover {
    color: ${({ theme }) => theme.gold};
    border-color: ${({ theme }) => theme.gold};
    box-shadow: 0 0 26px -6px ${({ theme }) => theme.goldGlow};
    transform: translateY(-2px);
  }
`;

/* ---------- scroll reveal — calm, recruiter-friendly ----------
   Reveals ONCE when first scrolled into view (then stops observing), so
   content stays put when you scroll back up — no distracting re-animation.
   A subtle fade + small rise; respects prefers-reduced-motion. */
export function useReveal(threshold = 0.14) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setShown(true);
          io.unobserve(el);
        }
      },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, shown];
}

/* Subtle fade + short rise — one direction, fast, never distracting.
   $dir/$delay are still accepted (so callers don't break) but $dir is
   intentionally ignored to keep the motion calm and consistent. */
export const Reveal = styled.div`
  opacity: ${({ $in }) => ($in ? 1 : 0)};
  transform: ${({ $in }) => ($in ? "translateY(0)" : "translateY(18px)")};
  transition: opacity 0.5s ease, transform 0.5s ease;
  transition-delay: ${({ $in, $delay = 0 }) => ($in ? `${$delay}s` : "0s")};
  will-change: opacity, transform;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;
  }
`;

/* thin divider with a centered rose-window slot (caller passes children) */
export const DividerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 22px;
  margin: 8px 0 4px;

  &::before,
  &::after {
    content: "";
    height: 1px;
    flex: 1;
    max-width: 220px;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => theme.border},
      transparent
    );
  }
`;
